import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useAppStore } from '@/stores/appStore';
import config from '@/config';
import type { AgentRunRequest, Event, Content } from '@/types';

// Query Keys
export const queryKeys = {
  health: ['health'] as const,
  sessions: (appName: string) => ['sessions', appName] as const,
  session: (appName: string, sessionId: string) =>
    ['session', appName, sessionId] as const,
  artifacts: (appName: string, sessionId: string) =>
    ['artifacts', appName, sessionId] as const,
  artifact: (appName: string, sessionId: string, artifactName: string, version?: number) =>
    ['artifact', appName, sessionId, artifactName, version] as const,
  corpus: ['corpus'] as const,
  availableOptions: ['availableOptions'] as const,
};

// Health Check Hook
export function useHealthCheck() {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => apiClient.healthCheck(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });
}

// Corpus Hook
export function useCorpus() {
  const { user } = useAppStore();

  return useQuery({
    queryKey: queryKeys.corpus,
    queryFn: () => apiClient.getCorpus(),
    enabled: !!user.id && user.authenticated,
    staleTime: 30 * 1000, // 30 seconds
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as any).status;
        if (status === 401 || status === 403) {
          return false;
        }
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    refetchOnWindowFocus: false, // Prevent excessive refetching
    refetchOnReconnect: true, // Refetch when network reconnects
  });
}

// Available Options Hook (for agent configuration)
export function useAvailableOptions() {
  const { user } = useAppStore();

  return useQuery({
    queryKey: queryKeys.availableOptions,
    queryFn: () => apiClient.getAvailableOptions(),
    enabled: !!user.id && user.authenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes - options don't change often
    retry: 3,
  });
}

// Session Hooks
export function useSessions() {
  const { user } = useAppStore();

  return useQuery({
    queryKey: queryKeys.sessions(config.APP_NAME),
    queryFn: () => apiClient.getSessions(),
    enabled: !!user.id && user.authenticated,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useSession(sessionId?: string) {
  const { user } = useAppStore();

  const enabled = !!user.id && user.authenticated && !!sessionId;

  return useQuery({
    queryKey: queryKeys.session(config.APP_NAME, sessionId || ''),
    queryFn: () => {
      if (!sessionId) {
        throw new Error('Session ID is required');
      }
      return apiClient.getSession(sessionId);
    },
    enabled,
    staleTime: 10 * 1000, // 10 seconds
  });
}

export function useCreateSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (state?: Record<string, any>) =>
      apiClient.createSession(state),
    onSuccess: () => {
      // Invalidate sessions list to refetch
      queryClient.invalidateQueries({
        queryKey: queryKeys.sessions(config.APP_NAME),
      });
    },
  });
}

export function useCreateSessionWithId() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, state }: { sessionId: string; state?: Record<string, any> }) =>
      apiClient.createSessionWithId(sessionId, state),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.sessions(config.APP_NAME),
      });
    },
  });
}



export function useDeleteSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) =>
      apiClient.deleteSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.sessions(config.APP_NAME),
      });
    },
  });
}

// Agent Communication Hooks
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, content, toolConfig }: { 
      sessionId?: string; 
      content: Content;
      toolConfig?: AgentRunRequest['toolConfig'];
    }) => {
      const request: AgentRunRequest = {
        sessionId,
        newMessage: content,
        streaming: false,
        toolConfig,
      };
      return apiClient.sendMessage(request);
    },
    onSuccess: (response) => {
      // Invalidate sessions list to refetch with new/updated session
      queryClient.invalidateQueries({
        queryKey: queryKeys.sessions(config.APP_NAME),
      });
      // Invalidate the specific session to refetch its data
      queryClient.invalidateQueries({
        queryKey: queryKeys.session(config.APP_NAME, response.sessionId),
      });
    },
    retry: (failureCount, error) => {
      // Don't retry on authentication or validation errors
      if (error && typeof error === 'object' && 'statusCode' in error) {
        const statusCode = (error as any).statusCode;
        if (statusCode === 401 || statusCode === 403 || statusCode === 400) {
          return false;
        }
      }
      // Retry up to 2 times for other errors (network, server errors)
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff, max 5s
  });
}

// Streaming Hook (custom implementation)
export function useStreamMessage() {
  const streamMessage = async function* (sessionId: string, content: Content): AsyncGenerator<Event> {
    const request: AgentRunRequest = {
      sessionId,
      newMessage: content,
      streaming: true,
    };

    yield* apiClient.streamMessage(request);
  };

  return { streamMessage };
}

// Artifact Hooks
export function useArtifacts(sessionId: string) {
  const { user } = useAppStore();

  return useQuery({
    queryKey: queryKeys.artifacts(config.APP_NAME, sessionId),
    queryFn: () => apiClient.listArtifacts(sessionId),
    enabled: !!user.id && user.authenticated && !!sessionId,
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useArtifact(sessionId: string, artifactName: string, version?: number) {
  const { user } = useAppStore();

  return useQuery({
    queryKey: queryKeys.artifact(config.APP_NAME, sessionId, artifactName, version),
    queryFn: () => apiClient.getArtifact(sessionId, artifactName, version),
    enabled: !!user.id && user.authenticated && !!sessionId && !!artifactName,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useDeleteArtifact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, artifactName }: { sessionId: string; artifactName: string }) =>
      apiClient.deleteArtifact(sessionId, artifactName),
    onSuccess: (_, { sessionId }) => {
      // Invalidate artifacts list for this session
      queryClient.invalidateQueries({
        queryKey: queryKeys.artifacts(config.APP_NAME, sessionId),
      });
    },
  });
}

// Feedback Hooks (placeholder for future implementation)
export function useSubmitFeedback() {
  return useMutation({
    mutationFn: ({ sessionId, eventId, feedbackType }: {
      sessionId: string;
      eventId: string;
      feedbackType: 'like' | 'dislike'
    }) => apiClient.submitFeedback(sessionId, eventId, feedbackType),
  });
}

// Document Upload Hook
export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => apiClient.uploadDocument(file),
    onSuccess: () => {
      // Invalidate corpus to refresh file list
      queryClient.invalidateQueries({
        queryKey: queryKeys.corpus,
      });
    },
  });
}